import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import type { DailyLog, FoodEntry, FoodSearchResult } from "@/types";

const todayIso = () => new Date().toISOString().slice(0, 10);
const RECENT_KEY_PREFIX = "pregtrack:recent-foods";

function scaleNutrients(per100g: Record<string, number>, grams: number): Record<string, number> {
  const factor = grams / 100;
  return Object.fromEntries(Object.entries(per100g).map(([k, v]) => [k, Math.round(v * factor * 10) / 10]));
}

export default function Log() {
  const [log, setLog] = useState<DailyLog | null>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<FoodSearchResult[]>([]);
  const [recent, setRecent] = useState<FoodEntry[]>([]);
  const [manualOpen, setManualOpen] = useState(false);

  useEffect(() => {
    api.logs.daily.get(todayIso()).then(setLog);
    const stored = localStorage.getItem(RECENT_KEY_PREFIX);
    if (stored) setRecent(JSON.parse(stored));
  }, []);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => {
      api.food.search(query.trim()).then(setResults).catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  function addEntry(entry: FoodEntry) {
    const nextEntries = [...(log?.foodEntries ?? []), entry];
    api.logs.daily.upsert(todayIso(), { foodEntries: nextEntries }).then(setLog);

    const nextRecent = [entry, ...recent.filter((r) => r.foodId !== entry.foodId)].slice(0, 8);
    setRecent(nextRecent);
    localStorage.setItem(RECENT_KEY_PREFIX, JSON.stringify(nextRecent));
  }

  function removeEntry(index: number) {
    const nextEntries = (log?.foodEntries ?? []).filter((_, i) => i !== index);
    api.logs.daily.upsert(todayIso(), { foodEntries: nextEntries }).then(setLog);
  }

  function addFromSearchResult(result: FoodSearchResult, grams = 100) {
    addEntry({
      foodId: result.externalId,
      name: result.name,
      qty: grams,
      unit: "g",
      nutrients: scaleNutrients(result.nutrientsPer100g, grams),
    });
    setQuery("");
    setResults([]);
  }

  return (
    <div className="mx-auto max-w-md px-4 pb-8 pt-[calc(env(safe-area-inset-top)+1.5rem)]">
      <h1 className="text-2xl font-semibold text-forest-700">Log food</h1>

      <div className="mt-4">
        <input
          className="input"
          placeholder="Search foods (e.g. greek yogurt)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {results.length > 0 && (
          <ul className="mt-2 divide-y divide-line rounded-card border border-line bg-white">
            {results.map((r) => (
              <li key={r.externalId}>
                <button
                  className="flex h-tap w-full items-center justify-between px-4 text-left text-sm text-ink"
                  onClick={() => addFromSearchResult(r)}
                >
                  <span>{r.name}</span>
                  <span className="text-forest-500">+ 100g</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {recent.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-medium text-ink">Log again</h2>
          <div className="flex flex-wrap gap-2">
            {recent.map((entry) => (
              <button
                key={entry.foodId}
                onClick={() => addEntry(entry)}
                className="chip"
              >
                {entry.name}
              </button>
            ))}
          </div>
        </section>
      )}

      <button
        onClick={() => setManualOpen(true)}
        className="btn-secondary mt-6"
      >
        Add manually
      </button>

      {manualOpen && (
        <ManualEntrySheet onClose={() => setManualOpen(false)} onAdd={(e) => { addEntry(e); setManualOpen(false); }} />
      )}

      <section className="mt-8">
        <h2 className="mb-2 text-sm font-medium text-ink">Today</h2>
        {(log?.foodEntries ?? []).length === 0 && (
          <p className="text-sm text-forest-500">Nothing logged yet today.</p>
        )}
        <ul className="divide-y divide-line rounded-card border border-line bg-white">
          {(log?.foodEntries ?? []).map((entry, i) => (
            <li key={`${entry.foodId}-${i}`} className="flex h-tap items-center justify-between px-4">
              <span className="text-sm text-ink">
                {entry.name} <span className="text-forest-500">· {entry.qty}{entry.unit}</span>
              </span>
              <button
                onClick={() => removeEntry(i)}
                aria-label={`Remove ${entry.name}`}
                className="text-forest-500"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function ManualEntrySheet({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: (entry: FoodEntry) => void;
}) {
  const [name, setName] = useState("");
  const [calories, setCalories] = useState("");
  const [protein, setProtein] = useState("");

  return (
    <div className="fixed inset-0 z-30 flex items-end bg-black/30" onClick={onClose}>
      <div
        className="w-full rounded-t-card bg-canvas p-6"
        style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-4 text-lg font-semibold text-forest-700">Add food manually</h2>
        <div className="flex flex-col gap-3">
          <input className="input" placeholder="Food name" value={name} onChange={(e) => setName(e.target.value)} />
          <input
            className="input"
            placeholder="Calories (kcal)"
            type="number"
            inputMode="decimal"
            value={calories}
            onChange={(e) => setCalories(e.target.value)}
          />
          <input
            className="input"
            placeholder="Protein (g)"
            type="number"
            inputMode="decimal"
            value={protein}
            onChange={(e) => setProtein(e.target.value)}
          />
          <button
            className="btn-primary mt-2"
            disabled={!name.trim()}
            onClick={() =>
              onAdd({
                foodId: `manual-${Date.now()}`,
                name: name.trim(),
                qty: 1,
                unit: "serving",
                nutrients: {
                  calories: Number(calories) || 0,
                  protein: Number(protein) || 0,
                },
              })
            }
          >
            Add to today
          </button>
        </div>
      </div>
    </div>
  );
}
